import React, { useMemo } from 'react';
import StatisticCard from './StatisticCard';
import StatsSkeleton from './StatsSkeleton';
import StatsError from './StatsError';

const StatisticsGrid = ({ 
  stats, 
  isLoading, 
  isError, 
  isUsersLoading, 
  isUsersError, 
  onRefresh 
}) => {
  // Memoize the statistics cards data to prevent unnecessary re-renders
  const statisticsCards = useMemo(() => {
    if (!stats) return [];

    const cards = [];
    const context = stats.context || {};

    // Basic claim counts - visible to all users
    if (stats.counts) {
      cards.push(
        {
          title: 'Total Claims',
          value: stats.counts.totalClaims || 0,
          icon: 'claims'
        },
        {
          title: 'Pending Claims',
          value: stats.counts.pendingClaims || 0,
          icon: 'pending'
        },
        {
          title: 'Approved Claims',
          value: stats.counts.approvedClaims || 0,
          icon: 'approved'
        }
      );
    }

    // Badge metrics with role-based visibility
    if (stats.badges) {
      // Processed claims - visible to all users
      cards.push({
        title: 'Processed Claims',
        value: stats.badges.totalProcessed || 0,
        icon: 'shield-check'
      });

      // SLA Compliance - only for Admin users (canViewSystemStats)
      if (context.canViewSystemStats && stats.badges.slaCompliance !== undefined) {
        cards.push({
          title: 'SLA Compliance',
          value: `${stats.badges.slaCompliance}%`,
          icon: 'user-check'
        });
      }

      // Active Users - only for Admin users (canViewUserStats)
      if (context.canViewUserStats && stats.badges.activeUsers !== undefined) {
        cards.push({
          title: 'Active Users',
          value: stats.badges.activeUsers || 0,
          icon: 'users'
        });
      }
    }

    return cards;
  }, [stats]);

  // Show loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="border-b border-silver-mist pb-6">
          <h2 className="text-2xl font-display font-bold text-deep-jungle tracking-tight">
            Dashboard Overview
          </h2>
          <p className="mt-2 text-sm text-steel">
            Key metrics and system statistics at a glance
          </p>
        </div>
        <StatsSkeleton />
      </div>
    );
  }

  // Show error state
  if (isError) {
    return (
      <div className="space-y-8">
        <div className="border-b border-silver-mist pb-6">
          <h2 className="text-2xl font-display font-bold text-deep-jungle tracking-tight">
            Dashboard Overview
          </h2>
          <p className="mt-2 text-sm text-steel">
            Key metrics and system statistics at a glance
          </p>
        </div>
        <StatsError message="Failed to load statistics. Please try refreshing the page." />
      </div>
    );
  }

  // Show statistics cards
  return (
    <div className="space-y-8">
      <div className="border-b border-silver-mist pb-6">
        <h2 className="text-2xl font-display font-bold text-deep-jungle tracking-tight">
          Dashboard Overview
        </h2>
        <p className="mt-2 text-sm text-steel">
          Key metrics and system statistics at a glance
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statisticsCards.map((card, index) => (
          <StatisticCard
            key={`statistic-card-${card.title.toLowerCase().replace(/\s+/g, '-')}-${index}`}
            title={card.title}
            value={card.value}
            icon={card.icon}
          />
        ))}
        
        {statisticsCards.length === 0 && !isLoading && !isError && (
          <div className="col-span-full text-center py-12">
            <p className="text-steel">No statistics available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(StatisticsGrid);
